/**
 * Outil « Mouvement et énergie » (Boîte à outils, physique-chimie 6e → Tle) — logique PURE.
 *
 * Six scènes : une voiture à vitesse constante (« Route »), qui démarre, qui freine (temps de
 * réaction puis freinage), une nacelle de manège (cercle à vitesse constante), une balle lâchée
 * (« Chute ») ou lancée (« Lancer »), avec ou sans frottements de l'air. `simulate` calcule le
 * mouvement (positions, vitesses) ; `chrono` donne la chronophotographie (positions à intervalles
 * de temps égaux) ; `velocities`, `deltaVs`, `accelerations` les vecteurs construits comme dans
 * les cartes (2nde : v⃗ᵢ ≈ MᵢMᵢ₊₁/Δt ; 1re : Δv⃗ = v⃗ᵢ₊₁ − v⃗ᵢ ; Tle : différences centrées) ;
 * `energies` les énergies cinétique, de position et mécanique ; `lawLines` les phrases du prof
 * selon le niveau ; `makeQuiz` les questions du défi.
 *
 * Conventions : x horizontal, z vertical vers le haut (sol : z = 0) ; pour le manège, (x, z) est
 * la vue de dessus. g = 9,8 N/kg comme dans les cartes.
 */

import {
  frNum,
  mcqOptions,
  pick,
  retryMcq,
  sigFixed,
  sigNum,
  type Mcq,
  type Rng,
} from '../../ui/mcq';

export type SceneId = 'road' | 'start' | 'brake' | 'merry' | 'drop' | 'throw';
export type SceneKind = 'car' | 'merry' | 'ball';

export interface SceneDef {
  id: SceneId;
  label: string;
  kind: SceneKind;
}

export const SCENES: readonly SceneDef[] = [
  { id: 'road', label: 'Route', kind: 'car' },
  { id: 'start', label: 'Démarrage', kind: 'car' },
  { id: 'brake', label: 'Freinage', kind: 'car' },
  { id: 'merry', label: 'Manège', kind: 'merry' },
  { id: 'drop', label: 'Chute', kind: 'ball' },
  { id: 'throw', label: 'Lancer', kind: 'ball' },
];

export const sceneById = (id: SceneId): SceneDef => SCENES.find((s) => s.id === id)!;

/** pesanteur (N/kg), comme dans les cartes */
export const G = 9.8;
/** longueur de route filmée (m) : « Route » et « Démarrage » */
export const ROAD_L = 100;
/** rayon du manège (m) */
export const MERRY_R = 5;
/** vitesse limite de la balle avec frottements (balle en mousse, m/s) */
export const V_LIM = 9;
/** décélération au freinage : route sèche / mouillée (m/s²) */
export const BRAKE_DRY = 7;
export const BRAKE_WET = 4;

export interface MotionInput {
  scene: SceneId;
  /** vitesse (m/s) : route, début du freinage, manège, lancer */
  v0: number;
  /** accélération du démarrage, décélération du freinage (m/s²) */
  a: number;
  /** temps de réaction avant de freiner (s) */
  reaction: number;
  /** hauteur de départ de la balle (m) */
  h: number;
  /** angle du lancer au-dessus de l'horizontale (°) */
  angle: number;
  /** masse (kg) */
  m: number;
  /** frottements de l'air (chute, lancer) */
  drag: boolean;
}

export interface State {
  t: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
}

export interface Sim {
  /** durée filmée (s) */
  T: number;
  /** états à pas de temps constant `dt` */
  states: State[];
  dt: number;
  /** freinage : distance de réaction, de freinage, d'arrêt (m) */
  dR: number;
  dF: number;
  /** sans frottements : formules exactes (sinon, intégration numérique) */
  exactOf: ((t: number) => State) | null;
}

export const speed = (s: Pick<State, 'vx' | 'vz'>): number => Math.hypot(s.vx, s.vz);

/** position et vitesse à l'instant t, sans frottements (formules exactes) */
function exact(input: MotionInput, t: number): State {
  const { v0, a } = input;
  switch (input.scene) {
    case 'road':
      return { t, x: v0 * t, z: 0, vx: v0, vz: 0 };
    case 'start':
      return { t, x: 0.5 * a * t * t, z: 0, vx: a * t, vz: 0 };
    case 'brake': {
      const tR = input.reaction;
      if (t <= tR) return { t, x: v0 * t, z: 0, vx: v0, vz: 0 };
      const u = Math.min(t - tR, v0 / a);
      return { t, x: v0 * tR + v0 * u - 0.5 * a * u * u, z: 0, vx: v0 - a * u, vz: 0 };
    }
    case 'merry': {
      const w = v0 / MERRY_R;
      const th = w * t;
      // départ en bas du cercle (vue de dessus), sens inverse des aiguilles d'une montre
      return {
        t,
        x: MERRY_R * Math.sin(th),
        z: -MERRY_R * Math.cos(th),
        vx: v0 * Math.cos(th),
        vz: v0 * Math.sin(th),
      };
    }
    case 'drop':
      return { t, x: 0, z: input.h - 0.5 * G * t * t, vx: 0, vz: -G * t };
    case 'throw': {
      const r = (input.angle * Math.PI) / 180;
      const vx = v0 * Math.cos(r);
      const vz0 = v0 * Math.sin(r);
      return { t, x: vx * t, z: input.h + vz0 * t - 0.5 * G * t * t, vx, vz: vz0 - G * t };
    }
  }
}

/** durée sans frottements : fin de la route, arrêt, un tour, arrivée au sol */
function exactDuration(input: MotionInput): number {
  const { v0, a, h } = input;
  switch (input.scene) {
    case 'road':
      return ROAD_L / v0;
    case 'start':
      return Math.sqrt((2 * ROAD_L) / a);
    case 'brake':
      return input.reaction + v0 / a;
    case 'merry':
      return (2 * Math.PI * MERRY_R) / v0;
    case 'drop':
      return Math.sqrt((2 * h) / G);
    case 'throw': {
      const vz0 = v0 * Math.sin((input.angle * Math.PI) / 180);
      return (vz0 + Math.sqrt(vz0 * vz0 + 2 * G * h)) / G;
    }
  }
}

const usesDrag = (input: MotionInput) =>
  input.drag && (input.scene === 'drop' || input.scene === 'throw');

/** accélération d'une balle freinée par l'air : frottement ∝ v², vitesse limite V_LIM */
function dragAcc(vx: number, vz: number): [number, number] {
  const v = Math.hypot(vx, vz);
  const k = G / (V_LIM * V_LIM);
  return [-k * v * vx, -G - k * v * vz];
}

export function simulate(input: MotionInput): Sim {
  const dR = input.scene === 'brake' ? input.v0 * input.reaction : 0;
  const dF = input.scene === 'brake' ? (input.v0 * input.v0) / (2 * input.a) : 0;
  if (!usesDrag(input)) {
    const T = exactDuration(input);
    const n = 600;
    const dt = T / n;
    const states = Array.from({ length: n + 1 }, (_, i) => exact(input, i * dt));
    // la balle s'arrête au sol (z = 0 exactement)
    const last = states[n]!;
    if (input.scene === 'drop' || input.scene === 'throw') states[n] = { ...last, z: 0 };
    return {
      T,
      states,
      dt,
      dR,
      dF,
      exactOf: (t) => {
        const s = exact(input, t);
        return t >= T && sceneById(input.scene).kind === 'ball' ? { ...s, z: 0 } : s;
      },
    };
  }
  // frottements : Runge-Kutta 4 jusqu'au sol
  const dt = 0.002;
  const r = input.scene === 'throw' ? (input.angle * Math.PI) / 180 : 0;
  let s: State = {
    t: 0,
    x: 0,
    z: input.h,
    vx: input.scene === 'throw' ? input.v0 * Math.cos(r) : 0,
    vz: input.scene === 'throw' ? input.v0 * Math.sin(r) : 0,
  };
  const states: State[] = [s];
  const f = (st: State) => {
    const [ax, az] = dragAcc(st.vx, st.vz);
    return [st.vx, st.vz, ax, az] as const;
  };
  for (let i = 0; i < 20000; i++) {
    const k1 = f(s);
    const s2 = {
      ...s,
      x: s.x + (dt / 2) * k1[0],
      z: s.z + (dt / 2) * k1[1],
      vx: s.vx + (dt / 2) * k1[2],
      vz: s.vz + (dt / 2) * k1[3],
    };
    const k2 = f(s2);
    const s3 = {
      ...s,
      x: s.x + (dt / 2) * k2[0],
      z: s.z + (dt / 2) * k2[1],
      vx: s.vx + (dt / 2) * k2[2],
      vz: s.vz + (dt / 2) * k2[3],
    };
    const k3 = f(s3);
    const s4 = {
      ...s,
      x: s.x + dt * k3[0],
      z: s.z + dt * k3[1],
      vx: s.vx + dt * k3[2],
      vz: s.vz + dt * k3[3],
    };
    const k4 = f(s4);
    const next: State = {
      t: s.t + dt,
      x: s.x + (dt / 6) * (k1[0] + 2 * k2[0] + 2 * k3[0] + k4[0]),
      z: s.z + (dt / 6) * (k1[1] + 2 * k2[1] + 2 * k3[1] + k4[1]),
      vx: s.vx + (dt / 6) * (k1[2] + 2 * k2[2] + 2 * k3[2] + k4[2]),
      vz: s.vz + (dt / 6) * (k1[3] + 2 * k2[3] + 2 * k3[3] + k4[3]),
    };
    if (next.z <= 0) {
      // arrivée au sol : interpolation linéaire, puis un dernier état au pas constant
      const u = s.z / (s.z - next.z);
      const land: State = {
        t: s.t + u * dt,
        x: s.x + u * (next.x - s.x),
        z: 0,
        vx: s.vx + u * (next.vx - s.vx),
        vz: s.vz + u * (next.vz - s.vz),
      };
      states.push({ ...land, t: s.t + dt });
      return { T: land.t, states, dt, dR, dF, exactOf: null };
    }
    states.push(next);
    s = next;
  }
  return { T: s.t, states, dt, dR, dF, exactOf: null };
}

/** état à l'instant t (interpolé), t ramené dans [0, T] */
export function stateAt(sim: Sim, t: number): State {
  const tt = Math.min(Math.max(t, 0), sim.T);
  if (sim.exactOf) return sim.exactOf(tt);
  const i = Math.min(Math.floor(tt / sim.dt), sim.states.length - 2);
  const a = sim.states[i]!;
  const b = sim.states[i + 1] ?? a;
  const u = Math.min(1, Math.max(0, (tt - a.t) / sim.dt));
  const lerp = (p: number, q: number) => p + u * (q - p);
  return {
    t: tt,
    x: lerp(a.x, b.x),
    z: lerp(a.z, b.z),
    vx: lerp(a.vx, b.vx),
    vz: lerp(a.vz, b.vz),
  };
}

/* ---------- chronophotographie et vecteurs ---------- */

const TAUS = [0.02, 0.04, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5] as const;

/** intervalle de la chronophotographie : une valeur ronde, une douzaine d'images au plus */
export function chronoTau(T: number): number {
  return TAUS.find((d) => T / d <= 12) ?? 5;
}

export interface Point {
  i: number;
  t: number;
  x: number;
  z: number;
}

/** positions M₀, M₁… prises toutes les `tau` secondes */
export function chrono(sim: Sim, tau = chronoTau(sim.T)): Point[] {
  const out: Point[] = [];
  for (let i = 0; i * tau <= sim.T + 1e-9; i++) {
    const s = stateAt(sim, i * tau);
    out.push({ i, t: i * tau, x: s.x, z: s.z });
  }
  return out;
}

export interface Vec {
  /** point d'application */
  at: Point;
  x: number;
  z: number;
}

/** vecteurs vitesse : 2nde / 1re v⃗ᵢ ≈ MᵢMᵢ₊₁ / Δt ; Tle (centrés) v⃗ᵢ ≈ Mᵢ₋₁Mᵢ₊₁ / 2Δt */
export function velocities(pts: Point[], tau: number, centered = false): Vec[] {
  const out: Vec[] = [];
  if (centered) {
    for (let i = 1; i < pts.length - 1; i++) {
      const p = pts[i - 1]!;
      const q = pts[i + 1]!;
      out.push({ at: pts[i]!, x: (q.x - p.x) / (2 * tau), z: (q.z - p.z) / (2 * tau) });
    }
    return out;
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const p = pts[i]!;
    const q = pts[i + 1]!;
    out.push({ at: p, x: (q.x - p.x) / tau, z: (q.z - p.z) / tau });
  }
  return out;
}

/** 1re : Δv⃗ = v⃗ᵢ₊₁ − v⃗ᵢ (vitesses « en avant »), tracé en Mᵢ₊₁, entre les deux vecteurs */
export function deltaVs(pts: Point[], tau: number): Vec[] {
  const v = velocities(pts, tau);
  const out: Vec[] = [];
  for (let i = 0; i < v.length - 1; i++) {
    out.push({ at: pts[i + 1]!, x: v[i + 1]!.x - v[i]!.x, z: v[i + 1]!.z - v[i]!.z });
  }
  return out;
}

/** Tle : a⃗ᵢ ≈ (v⃗ᵢ₊₁ − v⃗ᵢ₋₁) / 2Δt, avec les vitesses centrées */
export function accelerations(pts: Point[], tau: number): Vec[] {
  const v = velocities(pts, tau, true); // v[k] est en M(k+1)
  const out: Vec[] = [];
  for (let k = 1; k < v.length - 1; k++) {
    out.push({
      at: pts[k + 1]!,
      x: (v[k + 1]!.x - v[k - 1]!.x) / (2 * tau),
      z: (v[k + 1]!.z - v[k - 1]!.z) / (2 * tau),
    });
  }
  return out;
}

/* ---------- énergies ---------- */

export interface Energies {
  Ec: number;
  Ep: number;
  Em: number;
  /** énergie mécanique perdue (devenue thermique : freins, air), ≥ 0 */
  lost: number;
  /** énergie fournie par le moteur (démarrage), ≥ 0 */
  engine: number;
}

export function energies(input: MotionInput, sim: Sim, t: number): Energies {
  const ball = sceneById(input.scene).kind === 'ball';
  const em = (s: State) => 0.5 * input.m * speed(s) ** 2 + (ball ? input.m * G * s.z : 0);
  const s = stateAt(sim, t);
  const Ec = 0.5 * input.m * speed(s) ** 2;
  const Ep = ball ? input.m * G * s.z : 0;
  const d = em(sim.states[0]!) - (Ec + Ep);
  return {
    Ec,
    Ep,
    Em: Ec + Ep,
    lost: d > 1e-9 ? d : 0,
    engine: d < -1e-9 ? -d : 0,
  };
}

/* ---------- écriture ---------- */

const NB = String.fromCharCode(0xa0);
export const fr = frNum;
export const sig = sigNum;

/** unités de vitesse et d'accélération : « m/s » au collège et en 2nde / 1re, « m·s⁻¹ » en Tle */
export const VUNIT = (rank: number | null) => (rank !== null && rank >= 7 ? 'm·s⁻¹' : 'm/s');
export const AUNIT = (rank: number | null) => (rank !== null && rank >= 7 ? 'm·s⁻²' : 'm/s²');

export function ms(v: number, rank: number | null = null): string {
  return `${sig(v)}${NB}${VUNIT(rank)}`;
}
export function kmh(v: number): string {
  return `${fr(v * 3.6, 0)}${NB}km/h`;
}
export function meters(d: number): string {
  return `${sig(d)}${NB}m`;
}
export function seconds(t: number): string {
  return `${sig(t)}${NB}s`;
}
export function joules(E: number): string {
  if (Math.abs(E) >= 1e4) return `${sig(E / 1000)}${NB}kJ`;
  return `${sig(E)}${NB}J`;
}

/* ---------- phrases du prof ---------- */

type Pace = 'uniform' | 'faster' | 'slower' | 'mixed';

/** allure d'après la chronophotographie : écarts égaux, croissants, décroissants */
export function paceOf(pts: Point[]): Pace {
  const gaps: number[] = [];
  for (let i = 0; i < pts.length - 1; i++)
    gaps.push(Math.hypot(pts[i + 1]!.x - pts[i]!.x, pts[i + 1]!.z - pts[i]!.z));
  if (gaps.length < 2) return 'uniform';
  const tol = 0.02 * Math.max(...gaps);
  const up = gaps.every((g, i) => i === 0 || g >= gaps[i - 1]! - tol);
  const down = gaps.every((g, i) => i === 0 || g <= gaps[i - 1]! + tol);
  const flat = Math.max(...gaps) - Math.min(...gaps) <= tol * 2;
  if (flat) return 'uniform';
  if (up) return 'faster';
  if (down) return 'slower';
  return 'mixed';
}

export interface LawContext {
  input: MotionInput;
  sim: Sim;
  /** instant affiché (s) */
  t: number;
  rank: number | null;
  /** référentiel lié à la voiture (scènes de voiture) */
  carFrame: boolean;
}

/**
 * Phrases sous la figure : trajectoire et allure (tous niveaux), calcul de vitesse (collège),
 * distance d'arrêt et énergies (3e), vecteurs vitesse et principe d'inertie (2nde), Δv⃗, travail
 * du poids et énergie mécanique (1re), équations horaires et accélération (Tle).
 */
export function lawLines({ input, sim, t, rank, carFrame }: LawContext): string[] {
  const r = rank;
  const all = r === null;
  const kind = sceneById(input.scene).kind;
  const out: string[] = [];
  const tau = chronoTau(sim.T);
  const pts = chrono(sim, tau);
  const pace = paceOf(pts);
  const V = VUNIT(r);
  const A = AUNIT(r);
  const v = (x: number) => ms(x, r);

  if (kind === 'car' && carFrame) {
    out.push(
      'Dans le référentiel de la voiture, la voiture est immobile : toutes ses positions sont confondues. Ce sont les arbres qui reculent.',
    );
    out.push(
      'Dans le référentiel de la route (terrestre), c’est la voiture qui avance : le mouvement dépend du référentiel.',
    );
    return out;
  }

  // trajectoire et allure
  const shape =
    kind === 'merry'
      ? 'un cercle : mouvement circulaire'
      : input.scene === 'throw'
        ? input.drag
          ? 'une courbe : mouvement curviligne'
          : 'une parabole : mouvement curviligne'
        : 'une droite : mouvement rectiligne';
  const paceText: Record<Pace, string> = {
    uniform:
      'les positions sont également espacées : la vitesse est constante, le mouvement est uniforme.',
    faster:
      'les positions sont de plus en plus espacées : la vitesse augmente, le mouvement est accéléré.',
    slower:
      'les positions sont de plus en plus rapprochées : la vitesse diminue, le mouvement est ralenti.',
    mixed: 'les écarts entre positions changent : la vitesse varie.',
  };
  let paceLine = paceText[pace];
  if (input.scene === 'brake')
    paceLine =
      'positions également espacées pendant le temps de réaction, puis de plus en plus rapprochées : la voiture ralentit jusqu’à l’arrêt.';
  if (input.scene === 'throw')
    paceLine = 'la vitesse diminue pendant la montée, puis augmente pendant la descente.';
  if (input.scene === 'drop' && input.drag)
    paceLine =
      'les écarts augmentent puis se stabilisent : la balle atteint presque sa vitesse limite.';
  out.push(`Trajectoire : ${shape} ; ${paceLine}`);

  // collège : calcul de vitesse
  if (all || (r !== null && r <= 4)) {
    if (input.scene === 'road')
      out.push(
        `v = d ÷ t = ${meters(ROAD_L)} ÷ ${seconds(sim.T)} ≈ ${v(input.v0)}, soit ${kmh(input.v0)}.`,
      );
    if (input.scene === 'start') {
      const vEnd = speed(sim.states[sim.states.length - 1]!);
      out.push(
        `Vitesse moyenne : v = d ÷ t = ${meters(ROAD_L)} ÷ ${seconds(sim.T)} ≈ ${v(ROAD_L / sim.T)} ; à l’arrivée, la voiture roule à ${v(vEnd)} (${kmh(vEnd)}).`,
      );
    }
    if (input.scene === 'merry')
      out.push(
        `Un tour : d = 2 × π × R = ${meters(2 * Math.PI * MERRY_R)} en ${seconds(sim.T)} : v ≈ ${v(input.v0)}. La valeur de la vitesse est constante, sa direction change sans cesse.`,
      );
  }

  // 3e : distance d'arrêt
  if ((all || (r !== null && r >= 4)) && input.scene === 'brake') {
    out.push(
      `d_A = d_R + d_F = ${meters(sim.dR)} + ${meters(sim.dF)} = ${meters(sim.dR + sim.dF)} (d_R = v × t_R = ${sig(input.v0)} × ${sig(input.reaction)}).`,
    );
    if (r === 4 || all)
      out.push(
        'Vitesse doublée : d_R double, d_F est multipliée par 4 (énergie cinétique 4 fois plus grande).',
      );
  }

  // énergies : 3e (E_c, E_p) et 1re / Tle (E_c, E_pp, E_m)
  const energyRank = all || r === 4 || (r !== null && r >= 6);
  if (energyRank) {
    const e = energies(input, sim, t);
    const s = stateAt(sim, t);
    const P = r !== null && r >= 6 ? 'E_pp' : 'E_p';
    const vt = speed(s);
    out.push(
      `À t = ${seconds(t)} : E_c = ½ × m × v² = ½ × ${fr(input.m, 2)} × ${sig(vt)}² ≈ ${joules(e.Ec)}` +
        (kind === 'ball'
          ? ` ; ${P} = m × g × ${r !== null && r >= 6 ? 'z' : 'h'} = ${fr(input.m, 2)} × 9,8 × ${sig(Math.max(s.z, 0))} ≈ ${joules(e.Ep)}.`
          : '.'),
    );
    if (kind === 'ball') {
      out.push(
        input.drag
          ? `E_m = E_c + ${P} diminue (${joules(e.lost)} perdus) : les frottements de l’air la transforment en énergie thermique.`
          : `E_m = E_c + ${P} ≈ ${joules(e.Em)} reste constante : sans frottements, l’énergie ${P === 'E_pp' ? 'potentielle' : 'de position'} se convertit en énergie cinétique.`,
      );
    }
    if (input.scene === 'brake')
      out.push(
        `En freinant, l’énergie cinétique devient énergie thermique (freins chauds) : ${joules(e.lost)} déjà convertis.`,
      );
    if (input.scene === 'start')
      out.push(
        `Le moteur fournit de l’énergie : E_c augmente (${joules(e.engine)} depuis le départ).`,
      );
  }

  // 2nde : vecteurs vitesse, principe d'inertie
  if (all || r === 5) {
    const vs = velocities(pts, tau);
    const k = Math.min(vs.length - 1, Math.max(0, Math.floor(t / tau)));
    const vk = vs[k];
    if (vk)
      out.push(
        `v⃗${sub(k)} ≈ M${sub(k)}M${sub(k + 1)} ÷ Δt = ${meters(Math.hypot(vk.x, vk.z) * tau)} ÷ ${seconds(tau)} ≈ ${v(Math.hypot(vk.x, vk.z))}.`,
      );
    out.push(
      input.scene === 'road'
        ? 'Vecteur vitesse constant : les forces qui s’exercent sur la voiture se compensent (principe d’inertie).'
        : 'Le vecteur vitesse varie : les forces qui s’exercent ne se compensent pas.',
    );
  }

  // 1re : Δv⃗ et forces, travail du poids
  if (all || r === 6) {
    if (kind === 'ball' && !input.drag)
      out.push(
        `Chute libre : Δv⃗ ≈ g⃗ × Δt, vertical vers le bas, de valeur 9,8 × ${fr(tau, 2)} ≈ ${v(G * tau)}.`,
      );
    if (kind === 'ball' && input.drag)
      out.push(
        'Avec les frottements, Δv⃗ raccourcit : la somme des forces (poids + frottements) diminue.',
      );
    if (input.scene === 'brake')
      out.push('Pendant le freinage, Δv⃗ est opposé au mouvement : la somme des forces aussi.');
    if (input.scene === 'merry')
      out.push('Δv⃗ est dirigé vers le centre du manège : la somme des forces aussi.');
    if (input.scene === 'start')
      out.push('Δv⃗ est dans le sens du mouvement : la somme des forces aussi.');
    if (input.scene === 'road') out.push('Δv⃗ = 0⃗ : la somme des forces est nulle.');
    if (kind === 'ball') {
      const s = stateAt(sim, t);
      const W = input.m * G * (input.h - s.z);
      out.push(
        `Travail du poids depuis le départ : W(P⃗) = m g (z_A − z) = ${fr(input.m, 2)} × 9,8 × (${sig(input.h)} − ${sig(Math.max(s.z, 0))}) ≈ ${joules(W)}.`,
      );
    }
  }

  // Tle : équations horaires, accélération
  if (all || r === 7) {
    switch (input.scene) {
      case 'road':
        out.push(`x(t) = v × t ; a⃗ = 0⃗ (mouvement rectiligne uniforme).`);
        break;
      case 'start':
        out.push(`MRUA : v(t) = a × t, x(t) = ½ a t² ; a = ${sig(input.a)} ${A}.`);
        break;
      case 'brake':
        out.push(
          `Freinage : v(t) = v₀ − a t, arrêt après v₀ / a = ${seconds(input.v0 / input.a)} ; a⃗ opposé au mouvement, a = ${sig(input.a)} ${A}.`,
        );
        break;
      case 'merry':
        out.push(
          `Mouvement circulaire uniforme : a = v² / R = ${sig(input.v0)}² / ${MERRY_R} ≈ ${sig(input.v0 ** 2 / MERRY_R)} ${A}, a⃗ vers le centre.`,
        );
        break;
      case 'drop':
        out.push(
          input.drag
            ? 'Avec frottements, a⃗ ≠ g⃗ : la valeur de a diminue jusqu’à 0 (vitesse limite).'
            : `z(t) = −½ g t² + h ; durée de chute t = √(2h / g) = ${seconds(sim.T)}.`,
        );
        break;
      case 'throw': {
        const rad = (input.angle * Math.PI) / 180;
        out.push(
          input.drag
            ? 'Avec frottements, a⃗ ≠ g⃗ : la trajectoire n’est plus une parabole (elle retombe plus court).'
            : `x(t) = v₀ cos α × t ; z(t) = −½ g t² + v₀ sin α × t + h ; sommet à t = v₀ sin α / g = ${seconds((input.v0 * Math.sin(rad)) / G)}.`,
        );
        break;
      }
    }
  }
  return out.map((l) => l.replaceAll(' m/s', ` ${V}`));
}

const SUB = '₀₁₂₃₄₅₆₇₈₉';
export const sub = (n: number) => String(n).replace(/\d/g, (d) => SUB[Number(d)]!);

/* ---------- défi ---------- */

export type QuizKind =
  | 'trajectory'
  | 'nature'
  | 'speed'
  | 'frame'
  | 'compare'
  | 'direction'
  | 'mean-speed'
  | 'chrono-speed'
  | 'kmh'
  | 'ec'
  | 'ep'
  | 'conservation'
  | 'stopping'
  | 'frame2'
  | 'vector-scale'
  | 'nature-vectors'
  | 'inertia'
  | 'dv-fall'
  | 'dv-direction'
  | 'work'
  | 'tec'
  | 'friction'
  | 'mrua'
  | 'centripetal'
  | 'fall-time'
  | 'apex'
  | 'tle-chrono';

export type Quiz = Mcq<QuizKind>;

/** cinq questions selon le niveau (6e = 1 … Tle = 7) */
export function quizKinds(rank: number | null): QuizKind[] {
  switch (rank) {
    case 1:
      return ['trajectory', 'nature', 'speed', 'frame', 'compare'];
    case 2:
      return ['nature', 'direction', 'mean-speed', 'chrono-speed', 'frame'];
    case 3:
      return ['speed', 'mean-speed', 'chrono-speed', 'direction', 'nature'];
    case 4:
      return ['kmh', 'ec', 'ep', 'conservation', 'stopping'];
    case 5:
      return ['frame2', 'chrono-speed', 'vector-scale', 'nature-vectors', 'inertia'];
    case 6:
      return ['dv-fall', 'dv-direction', 'work', 'tec', 'friction'];
    case 7:
      return ['mrua', 'centripetal', 'fall-time', 'apex', 'tle-chrono'];
    default:
      return ['nature', 'speed', 'ec', 'dv-fall', 'mrua'];
  }
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({
  kind,
  question,
  options: mcqOptions(good, wrongs, rng),
  explain,
});
const n2 = (v: number) => sigFixed(v, 2);
const n3 = (v: number) => sigFixed(v, 3);
const u = (v: string, unit: string) => `${v}${NB}${unit}`;

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'trajectory': {
      const [obj, good] = pick(
        [
          [
            'Une balle lancée vers l’avant, au-dessus d’un terrain',
            'Une courbe : mouvement curviligne',
          ],
          ['Une nacelle de grande roue', 'Un cercle : mouvement circulaire'],
          ['Une voiture sur une route parfaitement droite', 'Une droite : mouvement rectiligne'],
          ['Une pomme qui tombe d’un arbre, sans vent', 'Une droite : mouvement rectiligne'],
        ] as const,
        rng,
      );
      const all: W[] = [
        [
          'Une droite : mouvement rectiligne',
          'Regarde la forme du chemin suivi : est-ce une ligne droite ?',
        ],
        [
          'Un cercle : mouvement circulaire',
          'Un cercle : l’objet tourne autour d’un centre, toujours à la même distance.',
        ],
        [
          'Une courbe : mouvement curviligne',
          'Une courbe qui n’est pas un cercle : c’est le cas d’un objet lancé.',
        ],
        [
          'Un point : l’objet ne bouge pas',
          'L’objet se déplace par rapport au sol : sa trajectoire n’est pas un point.',
        ],
      ];
      return q(
        kind,
        `${obj}, observée depuis le sol. Quelle est la forme de sa trajectoire ?`,
        good,
        all.filter(([t]) => t !== good),
        'La trajectoire est la ligne formée par les positions successives de l’objet : droite (rectiligne), cercle (circulaire) ou autre courbe (curviligne).',
        rng,
      );
    }
    case 'nature': {
      const type = pick(['uniforme', 'accéléré', 'ralenti'] as const, rng);
      const g0 = pick([1.5, 2, 2.5, 3], rng);
      const step = pick([0.5, 1], rng);
      const gaps =
        type === 'uniforme'
          ? [g0, g0, g0, g0]
          : type === 'accéléré'
            ? [g0, g0 + step, g0 + 2 * step, g0 + 3 * step]
            : [g0 + 3 * step, g0 + 2 * step, g0 + step, g0];
      const good = `Rectiligne ${type}`;
      return q(
        kind,
        `Sur une chronophotographie (images prises à intervalles de temps égaux), un objet avance en ligne droite. Les écarts entre deux positions successives valent ${gaps.map((g) => `${fr(g, 1)} cm`).join(' ; ')}. Le mouvement est…`,
        good,
        [
          ['Rectiligne uniforme', 'Uniforme : les écarts seraient tous égaux.'],
          ['Rectiligne accéléré', 'Accéléré : les écarts augmenteraient.'],
          ['Rectiligne ralenti', 'Ralenti : les écarts diminueraient.'],
          ['Circulaire uniforme', 'L’objet avance en ligne droite : le mouvement est rectiligne.'],
        ].filter(([t]) => t !== good) as W[],
        'Pendant des durées égales, un écart plus grand veut dire une vitesse plus grande : écarts égaux, uniforme ; croissants, accéléré ; décroissants, ralenti.',
        rng,
      );
    }
    case 'speed': {
      const v = pick([2, 2.5, 4, 5, 6, 8, 10], rng);
      const tm = pick([1, 2, 3, 4, 5], rng);
      const d = v * 60 * tm;
      const who = pick(['Un cycliste', 'Une coureuse', 'Un robot de livraison', 'Un skateur'], rng);
      return q(
        kind,
        `${who} parcourt ${fr(d, 0)} m en ${tm} min, à vitesse constante. Quelle est sa vitesse en m/s ?`,
        u(fr(v, 1), 'm/s'),
        [
          [
            u(fr(d / tm, 1), 'm/s'),
            `Les minutes n’ont pas été converties : ${tm} min = ${tm * 60} s.`,
          ],
          [
            u(fr((60 * tm) / d, 3), 'm/s'),
            'La durée a été divisée par la distance : v = d ÷ t, pas t ÷ d.',
          ],
          [u(fr(d * 60 * tm, 0), 'm/s'), 'La distance a été multipliée par la durée : v = d ÷ t.'],
        ],
        `v = d ÷ t = ${fr(d, 0)} m ÷ ${tm * 60} s = ${fr(v, 1)} m/s (d en m et t en s donnent v en m/s).`,
        rng,
      );
    }
    case 'frame': {
      const [ques, good] = pick(
        [
          ['Léa est assise dans un train qui roule. Par rapport au train, Léa est…', 'Immobile'],
          [
            'Léa est assise dans un train qui roule. Par rapport au quai, Léa est…',
            'En mouvement vers l’avant du train',
          ],
          [
            'Léa est assise dans un train qui roule. Par rapport à Léa, le quai est…',
            'En mouvement vers l’arrière du train',
          ],
        ] as const,
        rng,
      );
      return q(
        kind,
        ques,
        good,
        [
          ['Immobile', 'Sa position change par rapport à l’objet de référence : il y a mouvement.'],
          [
            'En mouvement vers l’avant du train',
            'Regarde qui s’éloigne de qui, et dans quel sens.',
          ],
          [
            'En mouvement vers l’arrière du train',
            'Regarde qui s’éloigne de qui, et dans quel sens.',
          ],
          [
            'Ça dépend de la vitesse du train',
            'Immobile ou en mouvement ne dépend pas de la vitesse, mais de l’objet de référence.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'Un mouvement se décrit toujours par rapport à un objet de référence : Léa est immobile par rapport au train, en mouvement par rapport au quai.',
        rng,
      );
    }
    case 'compare': {
      const vA = pick([4, 5, 6], rng);
      const vB = pick([vA - 1, vA + 1], rng);
      const tA = pick([10, 20, 25], rng);
      const tB = pick([15, 30, 40], rng);
      const [a, b] = ['Ali', 'Zoé'];
      const good = vA > vB ? a : b;
      const bad = vA > vB ? b : a;
      return q(
        kind,
        `${a} parcourt ${vA * tA} m en ${tA} s ; ${b} parcourt ${vB * tB} m en ${tB} s. Qui va le plus vite ?`,
        good,
        [
          [
            bad,
            `Compare les vitesses, pas seulement les distances : ${a} ${vA} m/s, ${b} ${vB} m/s.`,
          ],
          ['Ils vont aussi vite', `Calcule v = d ÷ t pour chacun : ${vA} m/s et ${vB} m/s.`],
          [
            'On ne peut pas savoir',
            'On connaît la distance et la durée de chacun : on peut calculer leurs vitesses.',
          ],
        ],
        `${a} : ${vA * tA} ÷ ${tA} = ${vA} m/s ; ${b} : ${vB * tB} ÷ ${tB} = ${vB} m/s.`,
        rng,
      );
    }
    case 'direction': {
      const [obj, good] = pick(
        [
          [
            'Une nacelle de manège tourne à vitesse de valeur constante. Sa vitesse…',
            'Change de direction, pas de valeur',
          ],
          ['Une voiture freine en ligne droite. Sa vitesse…', 'Change de valeur, pas de direction'],
          [
            'Une balle lancée vers l’avant monte puis redescend. Sa vitesse…',
            'Change de valeur et de direction',
          ],
        ] as const,
        rng,
      );
      return q(
        kind,
        obj,
        good,
        [
          ['Ne change pas du tout', 'Une vitesse varie si sa valeur OU sa direction change.'],
          [
            'Change de direction, pas de valeur',
            'Regarde si l’objet tourne, et s’il va plus ou moins vite.',
          ],
          [
            'Change de valeur, pas de direction',
            'Regarde si l’objet tourne, et s’il va plus ou moins vite.',
          ],
          [
            'Change de valeur et de direction',
            'Regarde si l’objet tourne, et s’il va plus ou moins vite.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'La vitesse a une valeur et une direction : en virage, la direction change même si la valeur reste la même.',
        rng,
      );
    }
    case 'mean-speed': {
      const [T, label, wrongT] = pick(
        [
          [0.75, '45 min', 0.45],
          [1.25, '1 h 15 min', 1.15],
          [1.5, '1 h 30 min', 1.3],
          [2.5, '2 h 30 min', 2.3],
        ] as const,
        rng,
      );
      const v = pick([40, 60, 80, 100], rng);
      const d = v * T;
      return q(
        kind,
        `Un car parcourt ${fr(d, 1)} km en ${label}, pauses comprises. Quelle est sa vitesse moyenne ?`,
        u(fr(v, 0), 'km/h'),
        [
          [
            u(fr(d / wrongT, 0), 'km/h'),
            `${label} ne s’écrit pas ${fr(wrongT, 2)} h : une demi-heure vaut 0,5 h, un quart d’heure 0,25 h.`,
          ],
          [u(fr(d * T, 0), 'km/h'), 'La distance a été multipliée par la durée : v = d ÷ t.'],
          [
            u(fr(d, 1), 'km/h'),
            'La distance n’est pas une vitesse : il faut diviser par la durée.',
          ],
        ],
        `${label} = ${fr(T, 2)} h ; v_moy = ${fr(d, 1)} ÷ ${fr(T, 2)} = ${v} km/h.`,
        rng,
      );
    }
    case 'chrono-speed': {
      const lycee = rng() < 0.5;
      if (lycee) {
        const dt = pick([0.04, 0.05, 0.1], rng);
        const v = pick([1.2, 1.5, 2, 2.5], rng);
        const dcm = v * dt * 100;
        return q(
          kind,
          `Chronophotographie : Δt = ${fr(dt * 1000, 0)} ms entre deux images. Deux positions successives sont distantes, en réalité, de ${fr(dcm, 1)} cm. Quelle est la vitesse, en m/s ?`,
          u(n2(v), 'm/s'),
          [
            [u(n2(dcm / (dt * 1000)), 'm/s'), 'Convertis d’abord : cm en m et ms en s.'],
            [
              u(n3(dcm / dt), 'm/s'),
              `Les centimètres n’ont pas été convertis : ${fr(dcm, 1)} cm = ${fr(dcm / 100, 3)} m.`,
            ],
            [u(n2(dt / (dcm / 100)), 'm/s'), 'v = distance ÷ durée, pas l’inverse.'],
          ],
          `v = ${fr(dcm / 100, 3)} m ÷ ${fr(dt, 2)} s = ${n2(v)} m/s.`,
          rng,
        );
      }
      const dt = pick([0.5, 1, 2], rng);
      const v = pick([2, 3, 4, 5, 6], rng);
      const d = v * dt;
      return q(
        kind,
        `Chronophotographie d’un cycliste : une image toutes les ${fr(dt, 1)} s. Entre deux images, il avance de ${fr(d, 1)} m. Quelle est sa vitesse ?`,
        u(fr(v, 1), 'm/s'),
        [
          [u(fr(d * dt, 2), 'm/s'), 'La distance a été multipliée par la durée : v = d ÷ t.'],
          [u(fr(dt / d, 2), 'm/s'), 'v = distance ÷ durée, pas l’inverse.'],
          [
            u(fr(v, 1), 'km/h'),
            `${fr(d, 1)} m en ${fr(dt, 1)} s donne une vitesse en m/s, pas en km/h.`,
          ],
        ],
        `v = d ÷ t = ${fr(d, 1)} m ÷ ${fr(dt, 1)} s = ${fr(v, 1)} m/s.`,
        rng,
      );
    }
    case 'kmh': {
      const v = pick([10, 15, 20, 25, 30, 35], rng);
      const k = v * 3.6;
      return q(
        kind,
        `Convertis ${fr(k, 0)} km/h en m/s.`,
        u(fr(v, 0), 'm/s'),
        [
          [u(fr(k * 3.6, 1), 'm/s'), 'Pour passer des km/h aux m/s, on DIVISE par 3,6.'],
          [u(fr(k / 60, 2), 'm/s'), 'Une heure, c’est 3 600 s, pas 60 s : on divise par 3,6.'],
          [
            u(fr(k * 1000, 0), 'm/s'),
            'Les km ont été convertis en m, mais pas les heures en secondes.',
          ],
        ],
        `${fr(k, 0)} km/h = ${fr(k * 1000, 0)} m en 3 600 s, soit ${fr(k, 0)} ÷ 3,6 = ${fr(v, 0)} m/s.`,
        rng,
      );
    }
    case 'ec': {
      const m = pick([800, 1000, 1200, 1500], rng);
      const v = pick([10, 15, 20, 25, 30], rng);
      const inKmh = rng() < 0.5;
      const Ec = 0.5 * m * v * v;
      const k = v * 3.6;
      const wrongs: W[] = [
        [u(fr(m * v * v, 0), 'J'), 'Le ½ a été oublié : E_c = ½ × m × v².'],
        [u(fr(0.5 * m * v, 0), 'J'), 'La vitesse n’a pas été mise au carré.'],
      ];
      if (inKmh)
        wrongs.unshift([
          u(fr(0.5 * m * k * k, 0), 'J'),
          `La vitesse doit être en m/s : ${fr(k, 0)} km/h = ${v} m/s.`,
        ]);
      else wrongs.push([u(fr(0.25 * m * v * v, 0), 'J'), 'E_c = ½ × m × v², pas ½ × ½ × m × v².']);
      return q(
        kind,
        `Une voiture de ${fr(m, 0)} kg roule à ${inKmh ? `${fr(k, 0)} km/h` : `${v} m/s`}. Quelle est son énergie cinétique ?`,
        u(fr(Ec, 0), 'J'),
        wrongs,
        `E_c = ½ × ${fr(m, 0)} × ${v}² = ${fr(Ec, 0)} J (m en kg, v en m/s).`,
        rng,
      );
    }
    case 'ep': {
      const mg = pick([100, 200, 250, 500], rng);
      const h = pick([2, 3, 4, 5], rng);
      const m = mg / 1000;
      const Ep = m * G * h;
      return q(
        kind,
        `Une bille de ${mg} g est posée sur une étagère à ${h} m du sol (g = 9,8 N/kg). Quelle est son énergie de position (référence : le sol) ?`,
        u(n2(Ep), 'J'),
        [
          [u(fr(mg * G * h, 0), 'J'), `La masse doit être en kg : ${mg} g = ${fr(m, 2)} kg.`],
          [u(n2(m * h), 'J'), 'g a été oublié : E_p = m × g × h.'],
          [u(n2(m * G), 'J'), 'La hauteur a été oubliée : E_p = m × g × h.'],
        ],
        `E_p = m × g × h = ${fr(m, 2)} × 9,8 × ${h} ≈ ${n2(Ep)} J.`,
        rng,
      );
    }
    case 'conservation': {
      const Ep = pick([2, 4, 6, 8, 10], rng);
      const ground = rng() < 0.5;
      const good = ground ? Ep : Ep / 2;
      return q(
        kind,
        `Une bille est lâchée sans vitesse, sans frottements. En haut, E_p = ${Ep} J et E_c = 0 J. ${ground ? 'Juste avant le sol' : 'À mi-hauteur'}, que vaut E_c ?`,
        u(fr(good, 1), 'J'),
        [
          [u('0', 'J'), 'La bille accélère en tombant : son énergie cinétique augmente.'],
          [
            u(fr(2 * Ep, 1), 'J'),
            `Sans frottements, E_c + E_p reste égale à ${Ep} J : E_c ne peut pas dépasser ${Ep} J.`,
          ],
          [
            u(fr(ground ? Ep / 2 : Ep, 1), 'J'),
            ground
              ? 'Au sol, E_p = 0 : toute l’énergie est devenue cinétique.'
              : `À mi-hauteur, E_p vaut encore ${fr(Ep / 2, 1)} J.`,
          ],
        ],
        `Sans frottements, E_c + E_p = ${Ep} J tout au long de la chute ; ${ground ? 'au sol, E_p = 0 J' : `à mi-hauteur, E_p = ${fr(Ep / 2, 1)} J`}, donc E_c = ${fr(good, 1)} J.`,
        rng,
      );
    }
    case 'stopping': {
      if (rng() < 0.5) {
        const v = pick([10, 15, 20, 25], rng);
        const tr = pick([1, 1.5, 2], rng);
        const k = v * 3.6;
        return q(
          kind,
          `Une voiture roule à ${fr(k, 0)} km/h. Le conducteur réagit en ${fr(tr, 1)} s. Quelle distance parcourt-elle avant même de freiner ?`,
          u(fr(v * tr, 1), 'm'),
          [
            [u(fr(k * tr, 1), 'm'), `La vitesse doit être en m/s : ${fr(k, 0)} km/h = ${v} m/s.`],
            [u(fr(v / tr, 1), 'm'), 'd = v × t : on multiplie par le temps de réaction.'],
            [
              u('0', 'm'),
              'Pendant le temps de réaction, la voiture roule encore à pleine vitesse.',
            ],
          ],
          `d_R = v × t_R = ${v} m/s × ${fr(tr, 1)} s = ${fr(v * tr, 1)} m.`,
          rng,
        );
      }
      return q(
        kind,
        'Si la vitesse d’une voiture double, sa distance de freinage est multipliée par…',
        '4',
        [
          ['2', 'C’est vrai pour la distance de réaction, pas pour la distance de freinage.'],
          ['8', 'L’énergie cinétique dépend de v², pas de v³.'],
          [
            '1 (elle ne change pas)',
            'Plus d’énergie cinétique à dissiper : il faut plus de distance pour s’arrêter.',
          ],
        ],
        'L’énergie cinétique ½ m v² est multipliée par 4 quand v double : les freins doivent dissiper 4 fois plus d’énergie, sur une distance 4 fois plus grande.',
        rng,
      );
    }
    case 'frame2': {
      const [obj, good] = pick(
        [
          ['le mouvement de la Lune autour de la Terre', 'Géocentrique'],
          ['le mouvement de Mars autour du Soleil', 'Héliocentrique'],
          ['le mouvement d’un ballon sur un terrain de foot', 'Terrestre'],
          ['le mouvement d’un satellite de télécommunication', 'Géocentrique'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Quel référentiel choisir pour étudier ${obj} ?`,
        good,
        [
          [
            'Terrestre',
            'Le référentiel terrestre (lié au sol) sert aux objets qui bougent sur Terre, sur une courte durée.',
          ],
          [
            'Géocentrique',
            'Le référentiel géocentrique (centre de la Terre) sert à la Lune et aux satellites.',
          ],
          ['Héliocentrique', 'Le référentiel héliocentrique (centre du Soleil) sert aux planètes.'],
          [
            'Lié à l’objet étudié',
            'Dans son propre référentiel, l’objet est immobile : on n’étudie plus rien.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'On choisit le référentiel lié au corps autour duquel l’objet tourne : Terre (géocentrique), Soleil (héliocentrique) ; sur Terre, le sol (terrestre).',
        rng,
      );
    }
    case 'vector-scale': {
      const v = pick([1.5, 2, 3, 4.5, 6], rng);
      const sc = pick([0.5, 1, 1.5], rng);
      const L = v / sc;
      return q(
        kind,
        `Un point a une vitesse de ${fr(v, 1)} m/s. Échelle : 1 cm pour ${fr(sc, 1)} m/s. Quelle longueur donner au vecteur vitesse ?`,
        u(fr(L, 1), 'cm'),
        [
          [u(fr(v * sc, 2), 'cm'), 'On divise la vitesse par la valeur représentée par 1 cm.'],
          [u(fr(v, 1), 'cm'), 'L’échelle n’a pas été utilisée.'],
          [u(fr(L + 1, 1), 'cm'), `${fr(v, 1)} ÷ ${fr(sc, 1)} = ${fr(L, 1)} : refais la division.`],
        ],
        `Longueur = ${fr(v, 1)} ÷ ${fr(sc, 1)} = ${fr(L, 1)} cm, dans la direction et le sens du mouvement.`,
        rng,
      );
    }
    case 'nature-vectors': {
      const [desc, good] = pick(
        [
          ['identiques : même direction, même sens, même longueur', 'Rectiligne uniforme'],
          ['de même direction et de même sens, de plus en plus longs', 'Rectiligne accéléré'],
          ['de même direction et de même sens, de plus en plus courts', 'Rectiligne ralenti'],
          ['de même longueur, mais leur direction tourne', 'Circulaire uniforme'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Sur une chronophotographie, les vecteurs vitesse successifs sont ${desc}. Le mouvement est…`,
        good,
        [
          [
            'Rectiligne uniforme',
            'Uniforme ET rectiligne : vecteur vitesse constant (rien ne change).',
          ],
          ['Rectiligne accéléré', 'Accéléré : vecteurs de plus en plus longs.'],
          ['Rectiligne ralenti', 'Ralenti : vecteurs de plus en plus courts.'],
          ['Circulaire uniforme', 'Circulaire uniforme : même longueur, direction qui tourne.'],
        ].filter(([t]) => t !== good) as W[],
        'La longueur du vecteur vitesse donne la valeur de la vitesse, sa direction celle du mouvement.',
        rng,
      );
    }
    case 'inertia': {
      if (rng() < 0.5)
        return q(
          kind,
          'Un palet glisse sur une glace, sans frottement : les forces qui s’exercent sur lui se compensent. Son mouvement est…',
          'Rectiligne uniforme',
          [
            [
              'Ralenti, puis il s’arrête',
              'Sans frottement, rien ne le ralentit : les forces se compensent.',
            ],
            [
              'Rectiligne accéléré',
              'Une vitesse qui augmente demande des forces qui ne se compensent pas.',
            ],
            [
              'Circulaire uniforme',
              'Tourner demande une force vers le centre : les forces ne se compenseraient pas.',
            ],
          ],
          'Principe d’inertie : si les forces se compensent, un objet en mouvement garde un mouvement rectiligne uniforme.',
          rng,
        );
      return q(
        kind,
        'Une voiture roule en ligne droite à vitesse constante. Les forces qui s’exercent sur elle…',
        'Se compensent',
        [
          [
            'Ne se compensent pas : la force du moteur l’emporte',
            'Si elle l’emportait, la voiture accélérerait.',
          ],
          [
            'N’existent pas',
            'Le poids, la réaction de la route, les frottements… s’exercent, mais se compensent.',
          ],
          [
            'Se compensent seulement si la voiture est arrêtée',
            'Le principe d’inertie vaut aussi en mouvement rectiligne uniforme.',
          ],
        ],
        'Principe d’inertie : mouvement rectiligne uniforme (ou repos) ⇔ forces qui se compensent.',
        rng,
      );
    }
    case 'dv-fall': {
      if (rng() < 0.5) {
        const dt = pick([0.04, 0.05, 0.1, 0.2], rng);
        const dv = G * dt;
        return q(
          kind,
          `Une bille est en chute libre (g = 9,8 N/kg). Quelle est la valeur de la variation de sa vitesse pendant Δt = ${fr(dt, 2)} s ?`,
          u(n2(dv), 'm/s'),
          [
            [u('9,8', 'm/s'), 'Δv ≈ g × Δt : il faut multiplier par la durée.'],
            [u(n2(G / dt), 'm/s'), 'On multiplie g par Δt, on ne divise pas.'],
            [u(n2(dv / 10), 'm/s'), `Refais le calcul : 9,8 × ${fr(dt, 2)}.`],
          ],
          `Chute libre : Δv⃗ ≈ g⃗ × Δt, vertical vers le bas, de valeur 9,8 × ${fr(dt, 2)} ≈ ${n2(dv)} m/s.`,
          rng,
        );
      }
      return q(
        kind,
        'Une bille lancée verticalement vers le haut est en chute libre. Pendant la montée, le vecteur Δv⃗ est…',
        'Vertical, vers le bas',
        [
          [
            'Vertical, vers le haut',
            'La bille monte, mais ralentit : Δv⃗ est opposé à la vitesse, comme le poids.',
          ],
          ['Nul', 'Le poids agit en permanence : la vitesse change.'],
          ['Horizontal', 'Seul le poids agit : Δv⃗ a sa direction, verticale.'],
        ],
        'En chute libre, seul le poids agit : Δv⃗ ≈ g⃗ Δt est toujours vertical vers le bas, que la bille monte ou descende.',
        rng,
      );
    }
    case 'dv-direction': {
      const [ctx, good] = pick(
        [
          ['est dirigé vers le centre du cercle (manège)', 'Est dirigée vers le centre du cercle'],
          [
            'est opposé au mouvement (voiture qui freine en ligne droite)',
            'Est opposée au mouvement',
          ],
          ['est nul (voiture à vitesse constante en ligne droite)', 'Est nulle'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Sur une chronophotographie, le vecteur Δv⃗ ${ctx}. La somme des forces qui s’exercent sur le système…`,
        good,
        [
          [
            'Est dirigée vers le centre du cercle',
            'ΣF⃗ a la direction et le sens de Δv⃗ (m Δv⃗ / Δt ≈ ΣF⃗).',
          ],
          ['Est opposée au mouvement', 'ΣF⃗ a la direction et le sens de Δv⃗ (m Δv⃗ / Δt ≈ ΣF⃗).'],
          ['Est nulle', 'ΣF⃗ a la direction et le sens de Δv⃗ (m Δv⃗ / Δt ≈ ΣF⃗).'],
          [
            'Est dans le sens du mouvement',
            'ΣF⃗ a la direction et le sens de Δv⃗, pas forcément de la vitesse.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'm × Δv⃗ / Δt ≈ ΣF⃗ : la somme des forces a la direction et le sens du vecteur Δv⃗.',
        rng,
      );
    }
    case 'work': {
      const m = pick([0.2, 0.5, 2], rng);
      const zA0 = pick([3, 5, 8], rng);
      const zB0 = pick([1, 2], rng);
      const down = rng() < 0.5;
      const [zA, zB] = down ? [zA0, zB0] : [zB0, zA0];
      const W = m * G * (zA - zB);
      return q(
        kind,
        `Une balle de ${fr(m, 1)} kg passe de l’altitude z_A = ${zA} m à z_B = ${zB} m (axe vers le haut, g = 9,8 N/kg). Travail de son poids ?`,
        u(n2(W), 'J'),
        [
          [
            u(n2(-W), 'J'),
            down
              ? 'La balle descend : le poids va dans le sens du mouvement, son travail est positif.'
              : 'La balle monte : le poids s’oppose au mouvement, son travail est négatif.',
          ],
          [u(n2(m * G * zA), 'J'), 'Il faut la différence d’altitude z_A − z_B, pas z_A seule.'],
          [u(n2(m * G * (zA + zB)), 'J'), 'W = m g (z_A − z_B) : une différence, pas une somme.'],
        ],
        `W_AB(P⃗) = m g (z_A − z_B) = ${fr(m, 1)} × 9,8 × (${zA} − ${zB}) ≈ ${n2(W)} J.`,
        rng,
      );
    }
    case 'tec': {
      const h = pick([2, 5, 10, 20], rng);
      const v = Math.sqrt(2 * G * h);
      return q(
        kind,
        `Une bille est lâchée sans vitesse d’une hauteur de ${h} m, sans frottement (g = 9,8 N/kg). Avec quelle vitesse arrive-t-elle au sol ?`,
        u(n2(v), 'm/s'),
        [
          [u(n2(2 * G * h), 'm/s'), 'v² = 2 g h : il reste à prendre la racine carrée.'],
          [u(n2(Math.sqrt(G * h)), 'm/s'), '½ m v² = m g h donne v² = 2 g h : le 2 a été oublié.'],
          [u(n2(G * h), 'm/s'), 'v = √(2 g h), pas g × h.'],
        ],
        `Théorème de l’énergie cinétique : ½ m v² − 0 = m g h, donc v = √(2 × 9,8 × ${h}) ≈ ${n2(v)} m/s (la masse se simplifie).`,
        rng,
      );
    }
    case 'friction': {
      const m = pick([50, 60, 70], rng);
      const z = pick([10, 20, 30], rng);
      const vA = pick([0, 2], rng);
      const vB = pick([10, 12, 15], rng);
      const EmA = 0.5 * m * vA * vA + m * G * z;
      const EmB = 0.5 * m * vB * vB;
      const lost = EmA - EmB;
      const kJ = (E: number) => u(n2(E / 1000), 'kJ');
      return q(
        kind,
        `Un skieur de ${m} kg part à ${vA} m/s d’une altitude de ${z} m et arrive en bas (z = 0) à ${vB} m/s (g = 9,8 N/kg). Quelle énergie mécanique a-t-il perdue ?`,
        kJ(lost),
        [
          [kJ(EmA), 'C’est son énergie mécanique au départ ; il faut retrancher celle d’arrivée.'],
          [
            kJ(EmB),
            'C’est son énergie mécanique à l’arrivée ; il faut la retrancher à celle du départ.',
          ],
          [
            kJ(m * G * z - 0.5 * m * vB * vB + (vA ? 0 : 1000)),
            'Compte l’énergie cinétique de départ et d’arrivée, et l’énergie potentielle de départ.',
          ],
        ],
        `E_m(départ) = ½ × ${m} × ${vA}² + ${m} × 9,8 × ${z} ≈ ${n3(EmA)} J ; E_m(arrivée) = ½ × ${m} × ${vB}² = ${n3(EmB)} J ; perte ≈ ${n2(lost / 1000)} kJ, devenue énergie thermique (frottements).`,
        rng,
      );
    }
    case 'mrua': {
      const a = pick([1.5, 2, 2.5, 3], rng);
      const t = pick([4, 5, 6], rng);
      if (rng() < 0.5)
        return q(
          kind,
          `Une voiture part de l’arrêt avec une accélération constante a = ${n2(a)} m·s⁻². Quelle est sa vitesse au bout de ${t} s ?`,
          u(n2(a * t), 'm·s⁻¹'),
          [
            [
              u(n2(0.5 * a * t * t), 'm·s⁻¹'),
              '½ a t² donne la distance parcourue, pas la vitesse.',
            ],
            [u(n2(a / t), 'm·s⁻¹'), 'v(t) = a × t : on multiplie.'],
            [u(n2(0.5 * a * t), 'm·s⁻¹'), 'v(t) = a × t, sans ½ (le ½ est dans x(t)).'],
          ],
          `MRUA départ arrêté : v(t) = a t = ${n2(a)} × ${t} = ${n2(a * t)} m·s⁻¹.`,
          rng,
        );
      return q(
        kind,
        `Une voiture part de l’arrêt avec une accélération constante a = ${n2(a)} m·s⁻². Quelle distance parcourt-elle en ${t} s ?`,
        u(n2(0.5 * a * t * t), 'm'),
        [
          [u(n2(a * t * t), 'm'), 'x(t) = ½ a t² : le ½ a été oublié.'],
          [u(n2(a * t), 'm'), 'a × t est la vitesse, pas la distance.'],
          [u(n2(0.5 * a * t), 'm'), 'x(t) = ½ a t² : le temps est au carré.'],
        ],
        `x(t) = ½ a t² = ½ × ${n2(a)} × ${t}² = ${n2(0.5 * a * t * t)} m.`,
        rng,
      );
    }
    case 'centripetal': {
      const R = pick([2, 4, 5, 8], rng);
      const v = pick([2, 3, 4, 6], rng);
      const a = (v * v) / R;
      return q(
        kind,
        `Une nacelle tourne sur un cercle de rayon R = ${n2(R)} m à vitesse constante v = ${n2(v)} m·s⁻¹. Valeur de son accélération ?`,
        u(n2(a), 'm·s⁻²'),
        [
          [u(n2(v / R), 'm·s⁻²'), 'a = v² / R : la vitesse est au carré.'],
          [u(n2(v * v * R), 'm·s⁻²'), 'a = v² / R : on divise par le rayon.'],
          [
            u('0', 'm·s⁻²'),
            'Vitesse de valeur constante ne veut pas dire accélération nulle : la direction change.',
          ],
        ],
        `Mouvement circulaire uniforme : a⃗ centripète, a = v² / R = ${n2(v)}² / ${n2(R)} = ${n2(a)} m·s⁻².`,
        rng,
      );
    }
    case 'fall-time': {
      const h = pick([5, 10, 20, 45, 80], rng);
      const t = Math.sqrt((2 * h) / G);
      return q(
        kind,
        `Un objet est lâché sans vitesse d’une hauteur h = ${n2(h)} m, en chute libre (g = 9,8 m·s⁻²). Durée de la chute ?`,
        u(n2(t), 's'),
        [
          [u(n2(Math.sqrt(h / G)), 's'), 'h = ½ g t² donne t = √(2h / g) : le 2 a été oublié.'],
          [u(n2((2 * h) / G), 's'), 't² = 2h / g : il reste à prendre la racine carrée.'],
          [
            u(n2(Math.sqrt(2 * G * h)), 's'),
            '√(2 g h) est la vitesse d’arrivée (en m·s⁻¹), pas la durée.',
          ],
          [u(n2(h / G), 's'), 't = √(2h / g), pas h / g.'],
        ],
        `z(t) = h − ½ g t² = 0 donne t = √(2h / g) = √(2 × ${n2(h)} / 9,8) ≈ ${n2(t)} s.`,
        rng,
      );
    }
    case 'apex': {
      const v0 = pick([8, 10, 12, 15], rng);
      const al = pick([30, 45, 60], rng);
      const r = (al * Math.PI) / 180;
      const t = (v0 * Math.sin(r)) / G;
      return q(
        kind,
        `Une balle est lancée avec v₀ = ${n2(v0)} m·s⁻¹, à α = ${al}° au-dessus de l’horizontale, en chute libre (g = 9,8 m·s⁻²). À quelle date atteint-elle le sommet ?`,
        u(n2(t), 's'),
        [
          [
            u(n2((v0 * Math.cos(r)) / G), 's'),
            'Au sommet, c’est la vitesse VERTICALE qui s’annule : v₀ sin α − g t = 0.',
          ],
          [u(n2(v0 / G), 's'), 'Seule la composante verticale v₀ sin α compte.'],
          [
            u(n2((v0 * Math.sin(r)) / (2 * G)), 's'),
            'v_z(t) = v₀ sin α − g t s’annule à t = v₀ sin α / g, sans ½.',
          ],
          [
            u(n2((2 * v0 * Math.sin(r)) / G), 's'),
            'C’est la date du retour à l’altitude de départ, le double de celle du sommet.',
          ],
        ],
        `v_z(t) = v₀ sin α − g t = 0 au sommet : t = ${n2(v0)} × sin ${al}° / 9,8 ≈ ${n2(t)} s.`,
        rng,
      );
    }
    case 'tle-chrono': {
      const dt = pick([0.04, 0.05, 0.1], rng);
      const v = pick([1.5, 2, 2.5, 3], rng);
      const d = v * 2 * dt; // M(i−1)M(i+1), en m
      return q(
        kind,
        `Chronophotographie : Δt = ${fr(dt * 1000, 0)} ms. La distance réelle M₂M₄ vaut ${n3(d * 100)} cm. Valeur approchée de v₃ ?`,
        u(n2(v), 'm·s⁻¹'),
        [
          [u(n2(d / dt), 'm·s⁻¹'), 'De M₂ à M₄ il y a DEUX intervalles : on divise par 2Δt.'],
          [
            u(n2((d * 100) / (2 * dt)), 'm·s⁻¹'),
            `Les centimètres n’ont pas été convertis en mètres.`,
          ],
          [u(n2(d / (4 * dt)), 'm·s⁻¹'), 'v₃ ≈ M₂M₄ / 2Δt : deux intervalles, pas quatre.'],
        ],
        `v₃ ≈ M₂M₄ / (2Δt) = ${n3(d)} / (2 × ${fr(dt, 2)}) = ${n2(v)} m·s⁻¹.`,
        rng,
      );
    }
  }
}
